import React, { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setDataSourceForScenario } from '../../reducer/CommonSlice'
import { Button, Form, Input, Popconfirm, Table, Dropdown, Space } from 'antd'
import './TableGrid.scss'

const TableGrid = ({
  columnsConfig,
  dataSource,
  dispatchUpdateDataSource,
  scroll1,
  pagination,
  onChange,
  disptachTableParam,
  expandIcon,
  filteredDataMapping,
  filteredData,
  setFilteredData,
  handleNegative = false
  // marketShareSum,
  // validateMarketShare
}) => {
  const dispatch = useDispatch()
  const EditableContext = React.createContext(null)

  const handleTableChange = (pagination, filters, sorter, extra) => {
    if (disptachTableParam) {
      disptachTableParam(pagination)
    }
    onChange(pagination, filters, sorter, extra)
  }

  const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm()
    return (
      <Form form={form} component={false}>
        <EditableContext.Provider value={form}>
          <tr {...props} />
        </EditableContext.Provider>
      </Form>
    )
  }

  const EditableCell = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    allowNegative, // New property to allow negative values
    ...restProps
  }) => {
    const [editing, setEditing] = useState(false)
    const inputRef = useRef(null)
    const form = useContext(EditableContext)

    useEffect(() => {
      if (editing) {
        inputRef.current?.focus()
      }
    }, [editing])

    // useEffect(() => {
    //   validateMarketShare(dataSource);
    // },[])
    const cleanData = (data) => {
      return data.replace(/[\r\n]+/g, '').trim()
    }

    const excelToColumn = async (
      event,
      dataIndex,
      record,
      filteredDataMapping = null,
      filteredData = null,
      handleNegative = false
    ) => {
      event.preventDefault()
      let newDataSource = [...dataSource]
      let newFilteredDataSource = [...filteredData]
      let startFilteredIndex = dataSource.findIndex((item) => item.key === record.key)
      if (filteredData.length) {
        startFilteredIndex = filteredData.findIndex((item) => item.key === record.key)
      }
      if (startFilteredIndex === -1) return

      // Get clipboard data
      const clipboardData = event.clipboardData || window.clipboardData
      const pastedData = clipboardData.getData('Text').split('\n').map(cleanData).filter(Boolean)
      if (!filteredData.length) {
        pastedData.forEach((value, i) => {
          if (startFilteredIndex + i < dataSource.length) {
            let dataIndexToUpdate = startFilteredIndex + i
            if (dataIndexToUpdate !== undefined) {
              let cleanedValue = value.replace(/,/g, '') // Remove commas
              if (!isNaN(cleanedValue)) {
                newDataSource[dataIndexToUpdate] = {
                  ...newDataSource[dataIndexToUpdate],
                  [dataIndex]: parseFloat(cleanedValue).toFixed(2)
                }
              }
            }
          }
        })
      } else {
        pastedData.forEach((value, i) => {
          if (startFilteredIndex + i < filteredData.length) {
            let dataIndexToUpdate = filteredDataMapping[startFilteredIndex + i]
            if (dataIndexToUpdate !== undefined) {
              let cleanedValue = value.replace(/,/g, '') // Remove commas
              if (!isNaN(cleanedValue)) {
                const numValue = parseFloat(cleanedValue)
                const updatedData = {
                  ...dataSource[dataIndexToUpdate],
                  [dataIndex]: (handleNegative && numValue) < 0 ? 0 : numValue.toFixed(2)
                }
                newDataSource[dataIndexToUpdate] = updatedData
                newFilteredDataSource[startFilteredIndex + i] = updatedData
              }
            }
          }
        })
      }
      // Dispatch the updated dataSource
      dispatchUpdateDataSource(newDataSource, record)
      setFilteredData(newFilteredDataSource)
    }

    const toggleEdit = () => {
      setEditing(!editing)
      form.setFieldsValue({
        [dataIndex]: record[dataIndex]
      })
    }
    const save = async () => {
      try {
        const values = await form.validateFields()
        if (handleNegative && values) {
          let value = parseFloat(values['potential_market_share'])
          value = value < 0 ? 0 : value
          values['potential_market_share'] = value
        }
        toggleEdit()
        handleSave({
          ...record,
          ...values
        })
      } catch (errInfo) {
        console.log('Save failed:', errInfo)
      }
    }
    let childNode = children
    if (editable) {
      childNode = editing ? (
        <Form.Item
          style={{ margin: 0 }}
          name={dataIndex}
          initialValue={record[dataIndex]}
          rules={[
            {
              required: true,
              message: `${title} is required.`
            },
            {
              validator: (_, value) => {
                // Check if negative values are allowed, if allowNegative is true or not provided
                if (allowNegative || allowNegative === undefined) {
                  return Promise.resolve() // No validation needed if negative values are allowed or property is not provided
                } else if (!allowNegative && value < 0) {
                  return Promise.reject('Invalid Input')
                }
                return Promise.resolve()
              }
            }
          ]}
        >
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            type="number"
            onPaste={(event) => {
              excelToColumn(
                event,
                dataIndex,
                record,
                filteredDataMapping,
                filteredData,
                handleNegative
              )
            }}
          />
        </Form.Item>
      ) : (
        <div className="editable-cell-value-wrap" onClick={toggleEdit}>
          {children}
        </div>
      )
    }

    return <td {...restProps}>{childNode}</td>
  }

  const handleSaveFilteredData = (row) => {
    const newData = [...filteredData]
    const index = newData.findIndex((item) => row.key === item.key)
    const item = newData[index]
    const updatedNewData = newData.map((data) => {
      if (data.key === row.key) {
        return {
          ...item,
          ...row
        }
      }
      return data
    })
    setFilteredData(updatedNewData)
  }

  const handleSave = (row) => {
    const newData = [...dataSource]
    const index = newData.findIndex((item) => row.key === item.key)
    const item = newData[index]
    newData.splice(index, 1, {
      ...item,
      ...row
    })
    dispatchUpdateDataSource(newData, row)
    handleSaveFilteredData(row)
  }

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell
    }
  }

  const columns = columnsConfig?.map((col) => {
    if (!col.editable) {
      return col
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
        allowNegative: col.allowNegative
      })
    }
  })

  return (
    <div className="reusable-grid">
      <Table
        components={components}
        className="custom-table"
        size="small"
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={dataSource}
        columns={columns}
        scroll={scroll1}
        pagination={pagination}
        onChange={handleTableChange}
        expandable={{ expandIcon }}
      />
    </div>
  )
}

export default TableGrid
